// shadcn-style Resizable primitive — wraps react-resizable-panels v4.
//
// Wrapper API preserved from the v2 era:
//   <ResizablePanelGroup direction="horizontal|vertical" autoSaveId="..." />
//   <ResizablePanel defaultSize={N} minSize={N} />
//   <ResizableHandle [withHandle] />
//
// Two things the wrapper translates because v4 broke them:
//
//   1) `autoSaveId` was removed from the library in v4. We re-implement the
//      same semantic with a localStorage shim around the new imperative API
//      (`groupRef.setLayout()` on mount + `onLayoutChanged` to persist).
//      Storage keys are namespaced `onestone:resizable:<autoSaveId>` so they
//      don't collide with any future library-native key.
//
//   2) v4 stopped emitting `data-panel-group-direction` and the per-handle
//      drag-state attributes. Old Tailwind `data-[...]:` selectors would
//      silently fail to match. We pass `direction` via a small Context so
//      the handle can apply the correct horizontal/vertical styles.

import { createContext, useContext, useEffect, useRef } from 'react';
import { GripVertical } from 'lucide-react';
import {
  Group,
  Panel,
  Separator,
  type GroupImperativeHandle,
  type GroupProps,
  type Layout,
  type PanelProps,
  type SeparatorProps,
} from 'react-resizable-panels';
import { cn } from './utils';

type Direction = 'horizontal' | 'vertical';

const DirectionContext = createContext<Direction>('horizontal');

export interface ResizablePanelGroupProps extends Omit<
  GroupProps,
  'orientation' | 'defaultLayout' | 'onLayoutChange' | 'onLayoutChanged' | 'groupRef'
> {
  direction: Direction;
  /** Persist panel sizes to localStorage under `onestone:resizable:<autoSaveId>`. */
  autoSaveId?: string;
}

export function ResizablePanelGroup({
  className,
  direction,
  autoSaveId,
  children,
  ...props
}: ResizablePanelGroupProps) {
  const groupRef = useRef<GroupImperativeHandle | null>(null);

  // Restore persisted layout once the imperative handle is wired. Effect
  // depends on autoSaveId so swapping panels in a SPA route gives the new
  // group its own layout.
  useEffect(() => {
    if (!autoSaveId) return;
    const handle = groupRef.current;
    if (!handle) return;
    try {
      const raw = localStorage.getItem(storageKey(autoSaveId));
      if (!raw) return;
      const layout = JSON.parse(raw) as Layout;
      handle.setLayout(layout);
    } catch {
      /* corrupt JSON or quota — fall back to defaultSize on Panel children */
    }
  }, [autoSaveId]);

  // Conditional spread avoids passing `undefined` under exactOptionalPropertyTypes.
  const persistenceProps = autoSaveId
    ? {
        onLayoutChanged: (layout: Layout) => {
          try {
            localStorage.setItem(storageKey(autoSaveId), JSON.stringify(layout));
          } catch {
            /* private mode / quota exceeded — skip persistence silently */
          }
        },
      }
    : {};

  return (
    <DirectionContext.Provider value={direction}>
      <Group
        orientation={direction}
        groupRef={groupRef}
        className={cn('flex h-full w-full', direction === 'vertical' && 'flex-col', className)}
        {...persistenceProps}
        {...props}
      >
        {children}
      </Group>
    </DirectionContext.Provider>
  );
}

export const ResizablePanel = Panel as (props: PanelProps) => React.JSX.Element;

export function ResizableHandle({
  withHandle = false,
  className,
  ...props
}: SeparatorProps & { withHandle?: boolean }) {
  const direction = useContext(DirectionContext);
  return (
    <Separator
      className={cn(
        'relative flex bg-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'transition-colors hover:bg-primary/40 active:bg-primary',
        direction === 'horizontal'
          ? 'w-px items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2'
          : 'h-px w-full after:absolute after:inset-x-0 after:top-1/2 after:h-1 after:-translate-y-1/2',
        className,
      )}
      {...props}
    >
      {withHandle ? (
        <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border border-border bg-background">
          <GripVertical className="h-2.5 w-2.5" aria-hidden="true" />
        </div>
      ) : null}
    </Separator>
  );
}

function storageKey(autoSaveId: string): string {
  return `onestone:resizable:${autoSaveId}`;
}
